import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import { toast } from 'react-hot-toast';
import { ArrowLeftIcon, CheckIcon } from '@heroicons/react/24/outline';

function buildTripUpdatePayload(trip) {
  return {
    trip_name: trip.trip_name?.trim() || '',
    trip_description: trip.trip_description ?? '',
    trip_type: trip.trip_type || 'recreational',
    start_port: trip.start_port ?? '',
    end_port: trip.end_port ?? '',
    weather_conditions: trip.weather_conditions ?? '',
    sea_conditions: trip.sea_conditions ?? '',
    is_private: Boolean(trip.is_private),
    allow_sharing: Boolean(trip.allow_sharing),
    safety_notifications_enabled: trip.safety_notifications_enabled !== false,
  };
}

function formatSaveError(error) {
  const data = error.response?.data;
  if (data?.errors?.length) {
    return data.errors.map((e) => e.msg || e.message).join('; ');
  }
  if (data?.error) return data.error;
  return error.message || 'Failed to save trip';
}

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
  background: ${theme.colors.background.primary};
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;

  &:hover {
    border-color: ${theme.colors.primary.main};
    color: ${theme.colors.primary.main};
  }
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const Form = styled.form`
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  padding: 2rem;
  border: 1px solid ${theme.colors.border.primary};
`;

const FormSection = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0 0 1rem 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${theme.colors.text.primary};
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 3px ${theme.colors.primary.light};
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 3px ${theme.colors.primary.light};
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 3px ${theme.colors.primary.light};
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: ${theme.colors.text.primary};
  font-size: 0.875rem;

  input {
    cursor: pointer;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'primary' ? `
    background: ${theme.colors.primary.main};
    color: white;
    &:hover {
      background: ${theme.colors.primary.dark};
    }
  ` : `
    background: transparent;
    border: 1px solid ${theme.colors.border.primary};
    color: ${theme.colors.text.secondary};
    &:hover {
      border-color: ${theme.colors.primary.main};
      color: ${theme.colors.primary.main};
    }
  `}
`;

const EditTripForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [trip, setTrip] = useState(null);

  useEffect(() => {
    loadTrip();
  }, [id]);

  const loadTrip = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/trips/${id}`);
      setTrip(response.data.trip || response.data);
    } catch (error) {
      console.error('Error loading trip:', error);
      toast.error('Failed to load trip');
      navigate('/trips');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!trip.trip_name?.trim()) {
      toast.error('Trip name is required');
      return;
    }

    try {
      setSaving(true);
      const payload = buildTripUpdatePayload(trip);
      await api.put(`/api/trips/${id}`, payload);
      toast.success('Trip updated successfully!');
      navigate(`/trips/${id}`);
    } catch (error) {
      console.error('Error saving trip:', error);
      toast.error(formatSaveError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setTrip(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '4rem', color: theme.colors.text.secondary }}>
          Loading trip...
        </div>
      </Container>
    );
  }

  if (!trip) {
    return null;
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate(`/trips/${id}`)}>
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Trip
        </BackButton>
        <Title>Edit Trip</Title>
      </Header>

      <Form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <FormSection>
          <SectionTitle>Basic Information</SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>Trip Name *</Label>
              <Input
                type="text"
                value={trip.trip_name || ''}
                onChange={(e) => handleChange('trip_name', e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Trip Type</Label>
              <Select
                value={trip.trip_type || 'recreational'}
                onChange={(e) => handleChange('trip_type', e.target.value)}
              >
                <option value="recreational">Recreational</option>
                <option value="commercial">Commercial</option>
                <option value="delivery">Delivery</option>
                <option value="maintenance">Maintenance</option>
                <option value="one_way">One Way</option>
                <option value="round_trip">Round Trip</option>
                <option value="manual">Manual</option>
                <option value="auto">Auto-Detected</option>
              </Select>
            </FormGroup>
          </FormGrid>

          <FormGroup style={{ marginTop: '1.5rem' }}>
            <Label>Description</Label>
            <TextArea
              value={trip.trip_description || ''}
              onChange={(e) => handleChange('trip_description', e.target.value)}
              placeholder="Add notes about this trip..."
            />
          </FormGroup>
        </FormSection>

        {/* Ports */}
        <FormSection>
          <SectionTitle>Ports</SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>Start Port</Label>
              <Input
                type="text"
                value={trip.start_port || ''}
                onChange={(e) => handleChange('start_port', e.target.value)}
                placeholder="e.g., Gordons Yacht Harbour"
              />
            </FormGroup>

            <FormGroup>
              <Label>End Port</Label>
              <Input
                type="text"
                value={trip.end_port || ''}
                onChange={(e) => handleChange('end_port', e.target.value)}
                placeholder="e.g., Marina Bay"
              />
            </FormGroup>
          </FormGrid>
        </FormSection>

        {/* Conditions */}
        <FormSection>
          <SectionTitle>Conditions</SectionTitle>
          <FormGrid>
            <FormGroup>
              <Label>Weather Conditions</Label>
              <Input
                type="text"
                value={trip.weather_conditions || ''}
                onChange={(e) => handleChange('weather_conditions', e.target.value)}
                placeholder="e.g., Sunny, light winds"
              />
            </FormGroup>

            <FormGroup>
              <Label>Sea Conditions</Label>
              <Input
                type="text"
                value={trip.sea_conditions || ''}
                onChange={(e) => handleChange('sea_conditions', e.target.value)}
                placeholder="e.g., Calm, 1-2 ft waves"
              />
            </FormGroup>
          </FormGrid>
        </FormSection>

        {/* Privacy & Notifications */}
        <FormSection>
          <SectionTitle>Privacy & Notifications</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={trip.is_private || false}
                onChange={(e) => handleChange('is_private', e.target.checked)}
              />
              Private Trip (only you can see it)
            </CheckboxLabel>

            <CheckboxLabel>
              <input
                type="checkbox"
                checked={trip.allow_sharing || false}
                onChange={(e) => handleChange('allow_sharing', e.target.checked)}
                disabled={trip.is_private}
              />
              Allow Sharing (generate shareable link)
            </CheckboxLabel>

            <CheckboxLabel>
              <input
                type="checkbox"
                checked={trip.safety_notifications_enabled !== false}
                onChange={(e) => handleChange('safety_notifications_enabled', e.target.checked)}
              />
              Enable Safety Notifications
            </CheckboxLabel>
          </div>
        </FormSection>

        {/* Action Buttons */}
        <ButtonGroup>
          <Button type="button" onClick={() => navigate(`/trips/${id}`)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            <CheckIcon className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </ButtonGroup>
      </Form>
    </Container>
  );
};

export default EditTripForm;

